import React, { ReactElement, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import CheckBoxSwitcher from 'shared/components/checkbox-switcher'
import useUser, { isCustomer } from 'shared/hooks/use-user'
import { DomainAssetsInterface } from 'shared/types/domain'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import { useAvailableSections } from 'modules/header/hooks/use-available-sections'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import ProfileLayout from 'modules/profile/components/profile-layout'
import { getProfilePageLayout } from 'modules/profile/layouts/get-profile-page-layout'
import {
  ColumnDefinitionInterface,
  Table,
} from 'modules/profile/manage-suscriptions/components/table/table'
import { notificationSettingsLocoKeys } from 'modules/profile/notifications/constants/notification-settings-loco-keys'
import { useNotificationSettings } from 'modules/profile/notifications/hooks/use-notification-settings'
import { useNotificationSettingsActions } from 'modules/profile/notifications/hooks/use-notification-settings-actions'
import {
  NotificationSettingInterface,
  NotificationTarget,
} from 'modules/profile/notifications/types/notification-settings-interface'
import { getAvailableSettings } from 'modules/profile/notifications/utils/notification-settings-utils'

const NotificationSettings = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const router = useRouter()
  const { data: availableSections } = useAvailableSections()

  const { notificationSettings, mutate, isFetching } = useNotificationSettings()
  const { updateNotificationSetting } = useNotificationSettingsActions()

  useEffect(() => {
    if (!isCustomDomain && user && isCustomer(user)) {
      router.push(getDashboardNotificationSettingsLink(user))
    }
  }, [user, router])

  const availableSettings = availableSections &&
    notificationSettings && {
      items: getAvailableSettings(notificationSettings, availableSections),
      hasMore: false,
    }

  const onToggleSetting = async (
    setting: NotificationSettingInterface,
    target: NotificationTarget,
  ) => {
    mutate(
      data =>
        data &&
        data.map(prevSetting =>
          prevSetting.eventType === setting.eventType
            ? {
                ...prevSetting,
                [target]: !setting[target],
              }
            : prevSetting,
        ),
      { revalidate: false },
    )

    try {
      await updateNotificationSetting({
        eventType: setting.eventType,
        channel: target,
        enabled: !setting[target] ? 'true' : 'false',
      })
      toast.success(t('profile.notifications.notifications_update_success'))
    } catch (err) {
      console.error(err)
      toast.error(t('core.error.internal_error_message'))

      mutate(
        data =>
          data &&
          data.map(prevSetting =>
            prevSetting.eventType === setting.eventType
              ? {
                  ...prevSetting,
                  [target]: setting[target],
                }
              : prevSetting,
          ),
        { revalidate: false },
      )
    }
  }

  const columns: ColumnDefinitionInterface<NotificationSettingInterface>[] = [
    {
      key: 'eventType',
      header: (
        <h5 className="hidden lg:block">
          {t('profile.notifications.new_comments_mentions_likes')}
        </h5>
      ),
      widthPercent: 75,
      onRender: setting => (
        <span
          title={t(notificationSettingsLocoKeys[setting.eventType])}
          className="text-wrap font-bold text-text-primary lg:truncate lg:text-nowrap lg:font-normal"
        >
          {t(notificationSettingsLocoKeys[setting.eventType])}
        </span>
      ),
    },
    {
      key: 'email',
      header: <h5>{t('profile.notifications.email')}</h5>,
      widthPercent: 10,
      onRender: setting => (
        <CheckBoxSwitcher
          selected={setting.email}
          onChange={() => onToggleSetting(setting, 'email')}
        />
      ),
    },
    {
      key: 'dashboard',
      header: <h5>{t('profile.notifications.in_app')}</h5>,
      widthPercent: 15,
      onRender: setting => (
        <CheckBoxSwitcher
          selected={setting.dashboard}
          onChange={() => onToggleSetting(setting, 'dashboard')}
        />
      ),
    },
  ]

  return (
    <ProfileLayout
      disabled={!notificationSettings?.length}
      title={t('profile.user_settings.manage_notifications.title')}
      isFetching={isFetching}
    >
      <div className="w-full">
        <div className="rounded-lg rounded-b-none bg-background-secondary px-6 py-4">
          <h3>{t('profile.user_settings.manage_notifications.title')}</h3>
          <span>{t('profile.user_settings.manage_notifications.description')}</span>
        </div>
        <Table data={availableSettings} columns={columns} className="rounded-none" />
      </div>
    </ProfileLayout>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

NotificationSettings.getLayout = (
  page: ReactElement,
  props: { domainAssets?: DomainAssetsInterface },
) =>
  getProfilePageLayout(page, {
    titleKey: 'profile.user_settings.manage_notifications.title',
    domainAssets: props.domainAssets,
  })

export default NotificationSettings
